<script lang="ts" setup>
    import type { PrimaryButtonProps } from '~/components/basics/PrimaryButton.vue';

    const props = defineProps<{ blok: SurveyButtonStoryblok } & Pick<PrimaryButtonProps, 'size' | 'stretch'>>();

    const consentModalController = useModal();
    const config = useRuntimeConfig();

    const INTERCEPT_SRC = 'https://intercept-widget.questionpro.eu/static/js/loader.js';
    const INTERCEPT_API_KEY = config.public.questionPro.apiKey as string;

    const { digest } = useUserDigest();

    const { hasConsent: hasSurveyConsent, refreshConsent } = useUsercentricsConsent('question_pro');
    const buttonId = computed(() => (props.blok.buttonId as { value?: string } | undefined)?.value);

    function loadInterceptScript() {
        if (!hasSurveyConsent.value) return;

        if (document.querySelector(`script[src="${INTERCEPT_SRC}"]`)) {
            return;
        }

        const w = window as unknown as Window;
        w.Intercept = { apiKey: INTERCEPT_API_KEY };

        const script = document.createElement('script');
        script.src = INTERCEPT_SRC;
        script.defer = true;
        document.head.appendChild(script);
    }

    async function handleClick(event: MouseEvent) {
        if (!hasSurveyConsent.value) {
            event.preventDefault();
            event.stopPropagation();

            let hasRefreshedConsent = false;

            try {
                hasRefreshedConsent = await refreshConsent();
            } catch {
                hasRefreshedConsent = false;
            }

            if (hasRefreshedConsent) {
                loadInterceptScript();
                return;
            }

            consentModalController.open();
            return;
        }
    }

    watch(hasSurveyConsent, (consent) => {
        if (consent) {
            loadInterceptScript();
        }
    });

    onMounted(() => {
        loadInterceptScript();
    });
</script>

<template>
    <div>
        <PrimaryButton
            :id="buttonId"
            v-editable="props.blok"
            :button-text="props.blok.text"
            :has-icon="props.blok.hasIcon"
            :disabled="props.blok.disabled"
            :variant="props.blok.alternateStyling ? 'secondary' : 'primary'"
            :size="props.blok?.size || undefined"
            :retain-size="props.blok.retainSize"
            :stretch="props.stretch"
            :alignment="props.blok?.alignment || undefined"
            @click="handleClick"
        />

        <div
            id="customerId"
            style="display: none"
            v-text="digest ?? ''"
        ></div>
    </div>

    <SurveyConsentModal :controller="consentModalController" />
</template>
